import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Switch,
  Typography,
  Badge,
  Modal,
  Empty,
  Alert,
  Progress,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchSchedules,
  deleteSchedule,
  updateSchedule,
  createSchedule,
  selectSchedules,
  selectTemplates,
  selectSegments,
  selectPushLoading,
} from 'store/slices/push.slice';
import { usePermissions } from 'hooks/usePermissions';
import { RecurringSchedule, ScheduleType } from 'types/push_management.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import RecurringScheduleLoader, { LoadedSchedule } from 'components/push/RecurringScheduleLoader';

const { confirm } = Modal;
const { Text } = Typography;

interface Props {
  appId: number | null;
}

interface ImportState {
  active: boolean;
  total: number;
  done: number;
  errors: string[];
}

const IMPORT_IDLE: ImportState = { active: false, total: 0, done: 0, errors: [] };
const RecurringScheduleList: React.FC<Props> = ({ appId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();
  const schedules = useAppSelector(selectSchedules);
  const templates = useAppSelector(selectTemplates);
  const segments = useAppSelector(selectSegments);
  const loading = useAppSelector(selectPushLoading);
  const [importState, setImportState] = useState<ImportState>(IMPORT_IDLE);
    const handleLoad = async (loaded: LoadedSchedule[], templateName?: string, _targetLang?: string) => {
    if (!appId) return;

    setImportState({ active: true, total: loaded.length, done: 0, errors: [] });

    const errors: string[] = [];

    for (let i = 0; i < loaded.length; i++) {
      const s = loaded[i];
      try {
        await dispatch(createSchedule({
          app_id: appId,
          name: s.name,
          description: s.description,
          schedule_type: s.schedule_type,
          schedule_value: s.schedule_value,
          schedule_time: s.schedule_time,
          timezone: s.timezone,
          is_active: s.is_active,
          start_date: new Date().toISOString(),
          push_template_id: s.push_template_id ?? 0,
          segment_id: s.segment_id,
        })).unwrap();
      } catch (err: any) {
        errors.push(`"${s.name}": ${err?.message ?? 'неизвестная ошибка'}`);
      }

      setImportState(prev => ({ ...prev, done: i + 1, errors }));
    }

    await dispatch(fetchSchedules(appId));

    const successCount = loaded.length - errors.length;

    if (errors.length === 0) {
      notify.success(
        `Импортировано ${successCount} расписаний${templateName ? ` из "${templateName}"` : ''}`
      );
      setImportState(IMPORT_IDLE);
    } else {
      setImportState(prev => ({ ...prev, active: false }));
      if (successCount > 0) {
        notify.success(`Импортировано ${successCount} из ${loaded.length} расписаний`);
      }
    }
  };

  const dismissImportErrors = () => setImportState(IMPORT_IDLE);
  const handleToggleActive = async (schedule: RecurringSchedule) => {
    try {
      await dispatch(updateSchedule({
        id: schedule.id,
        data: { is_active: !schedule.is_active },
      })).unwrap();
      notify.success(
        schedule.is_active ? 'Расписание деактивировано' : 'Расписание активировано'
      );
      if (appId) dispatch(fetchSchedules(appId));
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при обновлении расписания');
    }
  };

  const handleDelete = (schedule: RecurringSchedule) => {
    confirm({
      title: 'Удалить расписание?',
      content: `Вы уверены, что хотите удалить расписание "${schedule.name}"?`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteSchedule(schedule.id)).unwrap();
          notify.success('Расписание успешно удалено');
        } catch (error: any) {
          notify.error(error.message || 'Ошибка при удалении');
        }
      },
    });
  };

  const getScheduleTypeTag = (type: ScheduleType) => {
    const config: Record<ScheduleType, { color: string; text: string }> = {
      daily: { color: 'blue', text: 'Ежедневно' },
      weekly: { color: 'green', text: 'Еженедельно' },
      monthly: { color: 'orange', text: 'Ежемесячно' },
    };
    const { color, text } = config[type] || { color: 'default', text: type };
    return <Tag color={color} icon={<CalendarOutlined />}>{text}</Tag>;
  };

  const formatScheduleValue = (type: ScheduleType, value: string) => {
    const weekdayCodes: Record<string, string> = {
      MON: 'ПН', TUE: 'ВТ', WED: 'СР',
      THU: 'ЧТ', FRI: 'ПТ', SAT: 'СБ', SUN: 'ВС',
    };
    switch (type) {
      case 'daily': return `в ${value}`;
      case 'weekly': {
        const spaceIdx = value.lastIndexOf(' ');
        const dayCode = value.slice(0, spaceIdx);
        const time = value.slice(spaceIdx + 1);
        return `${weekdayCodes[dayCode] ?? dayCode} в ${time}`;
      }
      case 'monthly': {
        const [dayOfMonth, time] = value.split(' ');
        return `${dayOfMonth} числа в ${time ?? '?'}`;
      }
      default: return value;
    }
  };

  const getTemplateName = (templateId: number) =>
    templates.find(t => t.id === templateId)?.name || 'Неизвестный шаблон';

  const getSegmentName = (segmentId?: number) => {
    if (!segmentId) return 'Все пользователи';
    return segments.find(s => s.id === segmentId)?.name || 'Неизвестный сегмент';
  };

  const columns: ColumnsType<RecurringSchedule> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Шаблон: {getTemplateName(record.push_template_id)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'schedule_type',
      key: 'schedule_type',
      width: 140,
      render: (type: ScheduleType) => getScheduleTypeTag(type),
    },
    {
      title: 'Расписание',
      key: 'schedule',
      width: 160,
      render: (_, record) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#999' }} />
          <Text>{formatScheduleValue(record.schedule_type, record.schedule_value)}</Text>
        </Space>
      ),
    },
    {
      title: 'Timezone',
      dataIndex: 'timezone',
      key: 'timezone',
      width: 140,
      render: (tz: string) => (
        <Space>
          <GlobalOutlined style={{ color: '#999' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>{tz}</Text>
        </Space>
      ),
    },
    {
      title: 'Аудитория',
      key: 'audience',
      width: 160,
      render: (_, record) => (
        <Text type={record.segment_id ? undefined : 'secondary'}>
          {getSegmentName(record.segment_id)}
        </Text>
      ),
    },
    {
      title: 'След. запуск',
      dataIndex: 'next_run_at',
      key: 'next_run_at',
      width: 160,
      render: (date: string) => {
        if (!date) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={formatters.dateTime(date)}>
            <Badge status="processing" text={formatters.timeAgo(date)} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive: boolean, record) => (
        <Tooltip title={isActive ? 'Деактивировать' : 'Активировать'}>
          <Switch
            checked={isActive}
            onChange={() => handleToggleActive(record)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            disabled={!permissions.canManagePush}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(ROUTES.RECURRING_SCHEDULE_EDIT.replace(':id', String(record.id)))}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!appId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Выберите приложение для просмотра расписаний"
      />
    );
  }

  const importProgress = importState.total > 0
    ? Math.round((importState.done / importState.total) * 100)
    : 0;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {schedules.length > 0 ? `${schedules.length} расписаний` : 'Нет расписаний'}
        </Text>
        <Space>
          <RecurringScheduleLoader
            disabled={!appId || !permissions.canManagePush}
            appId={appId ?? undefined}
            segments={segments}
            templates={templates}
            onLoad={handleLoad}
          />
        </Space>
      </div>
      {importState.active && (
        <Alert
          type="info"
          showIcon
          message={
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text>
                Импорт расписаний: {importState.done} / {importState.total}
              </Text>
              <Progress
                percent={importProgress}
                size="small"
                status="active"
                style={{ marginBottom: 0 }}
              />
            </Space>
          }
        />
      )}
      {!importState.active && importState.errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={dismissImportErrors}
          message={`Не удалось импортировать ${importState.errors.length} расписаний`}
          description={
            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
              {importState.errors.map((e, i) => (
                <li key={i} style={{ fontSize: 12 }}>{e}</li>
              ))}
            </ul>
          }
        />
      )}
      <Table
        columns={columns}
        dataSource={schedules}
        loading={loading || importState.active}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Всего: ${total}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" align="center">
                  <Text>Расписания не найдены</Text>
                </Space>
              }
            />
          ),
        }}
        scroll={{ x: 1200 }}
      />
    </Space>
  );
};

export default RecurringScheduleList;